/**
 * Results tab — agent-streamed result previews rendered through
 * {@link AgenticDataGrid}. Newest first; the live tail of an in-flight
 * preview shows a "live" chip and rows append in place.
 *
 * Each result is its own grid (rather than a single big grid that
 * resets) so prior previews stay readable when the agent re-runs against
 * a new dataset or with new bindings.
 */

import { Box, Stack, Typography } from '@mui/material';

import type { EpiArtifact } from '../../types/epi-chat-types';
import { AgenticDataGrid } from '../AgenticDataGrid';

export const ResultsTab = ({ artifact }: { artifact: EpiArtifact }) => {
  const results = artifact.results ?? [];
  if (results.length === 0) {
    return (
      <Box sx={{ padding: 2 }}>
        <Typography variant="body2" color="text.secondary">
          No result previews yet. The agent will surface KPIs and sample rows here once it runs this
          analytical variable against a dataset.
        </Typography>
      </Box>
    );
  }
  return (
    <Stack spacing={2} sx={{ padding: 1.5 }} data-testid="artifact-results-tab">
      {results.map((result, idx) => (
        <Box key={result.resultId}>
          {idx > 0 && (
            <Typography
              variant="caption"
              color="text.secondary"
              sx={{ display: 'block', marginBottom: 0.5 }}
            >
              {new Date(result.computedAt).toLocaleString()}
            </Typography>
          )}
          <AgenticDataGrid result={result} testIdPrefix={`agentic-data-grid-${result.resultId}`} />
        </Box>
      ))}
    </Stack>
  );
};
